import { client } from '../configs/sanity';
import { urlFor } from '../configs/sanity';

const processImageUrlsSync = (data, imageFields, width) => {
  if (!data) return null;

  const processed = { ...data };

  imageFields.single?.forEach((field) => {
    if (data[field]) {
      processed[`${field}Url`] = urlFor(data[field]?.asset?._ref, { width });
      processed[`${field}Lqip`] = data[field]?.lqip || null;
    }
  });

  imageFields.arrays?.forEach(({ field, itemFields }) => {
    if (data[field]) {
      processed[field] = data[field].map((item) => {
        const processedItem = { ...item };
        itemFields.forEach((imgField) => {
          if (item[imgField]) {
            processedItem[`${imgField}Url`] = urlFor(
              item[imgField]?.asset?._ref,
              { width },
            );
            processedItem[`${imgField}Lqip`] = item[imgField]?.lqip || null;
          }
        });
        return processedItem;
      });
    }
  });

  return processed;
};

const fetchMenuContent = async () => {
  const query = `*[_type == "menu"][0]{
    _id,
    servicesLink,
    servicesLightImage,
    servicesDarkImage,
    homeLink,
    homeLightImage,
    homeDarkImage,
    aboutLink,
    aboutLightImage,
    aboutDarkImage,
    copyrightText,
  }`;

  try {
    const data = await client.fetch(query);
    if (!data) return data;

    return {
      ...data,
      servicesLightImageUrl: urlFor(data.servicesLightImage?.asset?._ref, {
        width: 1200,
      }),
      servicesDarkImageUrl: urlFor(data.servicesDarkImage?.asset?._ref, {
        width: 1200,
      }),
      homeLightImageUrl: urlFor(data.homeLightImage?.asset?._ref, {
        width: 1200,
      }),
      homeDarkImageUrl: urlFor(data.homeDarkImage?.asset?._ref, {
        width: 1200,
      }),
      aboutLightImageUrl: urlFor(data.aboutLightImage?.asset?._ref, {
        width: 1200,
      }),
      aboutDarkImageUrl: urlFor(data.aboutDarkImage?.asset?._ref, {
        width: 1200,
      }),
    };
  } catch (error) {
    console.error('Error fetching menu content:', error);
    return { error };
  }
};

let menuContentPromise = null;
export const getMenuContent = async () => {
  if (menuContentPromise) return menuContentPromise;
  menuContentPromise = fetchMenuContent();
  const result = await menuContentPromise;
  if (result?.error) menuContentPromise = null;
  return result;
};

const fetchFooterContent = async () => {
  const query = `*[_type == "footer"][0]{
    _id,
    linksColumn{
      title,
      links[]{_key, label, linkType, internalPath, externalUrl}
    },
    socialsColumn{
      title,
      links[]{_key, label, url}
    },
    infoColumn{title, address, email}
  }`;

  try {
    return await client.fetch(query);
  } catch (error) {
    console.error('Error fetching footer content:', error);
    return { error };
  }
};

let footerContentPromise = null;
export const getFooterContent = async () => {
  if (footerContentPromise) return footerContentPromise;
  footerContentPromise = fetchFooterContent();
  const result = await footerContentPromise;
  if (result?.error) footerContentPromise = null;
  return result;
};

// Fetched as its own small, fast request (separate from the page's big
// combined query below) so the hero can render as soon as this resolves,
// instead of waiting on the rest of the page's data too.
export const getHomeHeroData = async () => {
  const query = `*[_type == "homePageFirstSection"][0]{
    _id,
    title,
    description,
    buttonText,
    buttonLink,
    lightModeImage{ ..., "lqip": asset->metadata.lqip },
    darkModeImage{ ..., "lqip": asset->metadata.lqip },
    showLoader
  }`;

  try {
    const data = await client.fetch(query);
    return processImageUrlsSync(
      data,
      { single: ['lightModeImage', 'darkModeImage'] },
      1920,
    );
  } catch (error) {
    console.error('Error fetching home hero data:', error);
    return { error };
  }
};

// Same idea as getHomeHeroData, but for the generic "heroSection" type used
// by About/MainServices/B2CServices/B2BServices — each passes its own heroId.
export const getHeroSectionData = async (heroId) => {
  const query = `*[_type == "heroSection" && _id == "${heroId}"][0]{
    _id,
    title,
    titleHighlight,
    subtitle,
    description,
    buttonText,
    buttonLink,
    showThemeToggle,
    lightModeImage{ ..., "lqip": asset->metadata.lqip },
    darkModeImage{ ..., "lqip": asset->metadata.lqip },
  }`;

  try {
    const data = await client.fetch(query);
    return processImageUrlsSync(
      data,
      { single: ['lightModeImage', 'darkModeImage'] },
      1920,
    );
  } catch (error) {
    console.error('Error fetching hero section data:', error);
    return { error };
  }
};

export const getHomePageData = async (ids, onProgress) => {
  const {
    cardsCarouselId = 'd3c64963-cf45-4bcd-8b51-33ee6be53e4f',
    rollingMenuId = '44466eab-9bf7-4c28-bf60-844d852da17d',
    homeSliderId = 'ebdb22d2-27b2-4e2e-b1bf-607d8ec56ece',
    newsIconsId = '2f617b5d-e73e-457a-874b-a0f5a93dd280',
    thinImagesId = '8b9f30e8-7d78-42d5-bba5-3df8078407bc',
    brochureId = '18217150-e817-407a-bf98-d73835e77d14',
    servicesSectionId = '186b8796-b883-4b65-bec0-4bfa515581b9',
  } = ids || {};

  const query = `{
    "textPages": *[_type == "textPage"]{
      _id,
      type,
      titleActive,
      titleInactive,
      titleInactiveItalic,
      paragraphText,
      buttonText,
      buttonLink,
    },
    "cardsCarousel": *[_type == "imagesCarousel" && _id == "${cardsCarouselId}"][0]{
      _id,
      images[] {
        lightModeImage,
        darkModeImage,
        titleItalic,
        titleGrotesk,
        description,
        buttonText,
        buttonLink
      }
    },
    "homeLastPage": *[_type == "homeLastPage"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage,
    },
    "rollingMenu": *[_type == "rollingMenu" && _id == "${rollingMenuId}"][0]{
      _id,
      leftSideTitle,
      data[] {
        lightModeImage,
        darkModeImage,
        leftSideTitle,
        title,
        description
      }
    },
    "homeSlider": *[_type == "homeSlider" && _id == "${homeSliderId}"][0]{
      _id,
      slides[] {
        lightModeImage,
        darkModeImage,
        title,
        opacityTitle,
        italicTitle,
        description,
        buttonText,
        buttonLink
      }
    },
    "newsIcons": *[_type == "newsIcons" && _id == "${newsIconsId}"][0]{
      _id,
      items[] {
        icon,
        titleGrotesk,
        titleItalic,
        description
      }
    },
    "thinImages": *[_type == "thinImages" && _id == "${thinImagesId}"][0]{
      _id,
      italicTitle,
      groteskTitle,
      grid,
      data[] {
        "titleItalic": italicTitle,
        "titleGrotesk": groteskTitle,
        description,
        lightModeImage,
        darkModeImage
      }
    },
    "brochureData": *[_type == "brochureType" && _id == "${brochureId}"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage
    },
    "servicesSection": *[_type == "servicesSection" && _id == "${servicesSectionId}"][0]{
      _id,
      firstService {
        titleItalic,
        titleGrotesk,
        lightModeImage,
        darkModeImage,
      },
      secondService {
        titleItalic,
        titleGrotesk,
        lightModeImage,
        darkModeImage,
      }
    }
  }`;

  try {
    const data = await client.fetch(query);

    const processedData = {
      textPages: data.textPages,
      cardsCarousel: processImageUrlsSync(
        data.cardsCarousel,
        {
          arrays: [
            {
              field: 'images',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        1200,
      ),
      homeLastPage: processImageUrlsSync(
        data.homeLastPage,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      rollingMenu: processImageUrlsSync(
        data.rollingMenu,
        {
          arrays: [
            { field: 'data', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        1200,
      ),
      homeSlider: processImageUrlsSync(
        data.homeSlider,
        {
          arrays: [
            {
              field: 'slides',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        1200,
      ),
      newsIcons: processImageUrlsSync(data.newsIcons, {
        arrays: [{ field: 'items', itemFields: ['icon'] }],
      }),
      thinImages: processImageUrlsSync(
        data.thinImages,
        {
          arrays: [
            { field: 'data', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        800,
      ),
      brochureData: processImageUrlsSync(
        data.brochureData,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      servicesSection: data.servicesSection
        ? {
            ...data.servicesSection,
            firstService: data.servicesSection.firstService
              ? {
                  ...data.servicesSection.firstService,
                  lightModeImageUrl: urlFor(
                    data.servicesSection.firstService.lightModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                  darkModeImageUrl: urlFor(
                    data.servicesSection.firstService.darkModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                }
              : null,
            secondService: data.servicesSection.secondService
              ? {
                  ...data.servicesSection.secondService,
                  lightModeImageUrl: urlFor(
                    data.servicesSection.secondService.lightModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                  darkModeImageUrl: urlFor(
                    data.servicesSection.secondService.darkModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                }
              : null,
          }
        : null,
    };

    onProgress?.(1);

    return processedData;
  } catch (error) {
    console.error('Error fetching home page data:', error);
    return { error };
  }
};

export const getMainServicesData = async (ids, onProgress) => {
  const {
    ourRangeId = 'a10d9be4-bbba-4094-985d-bac29aa1978e',
    brochureId = '705fa18e-fd9e-4389-b0bc-0a755cb811b3',
    servicesSectionId = 'a0372f45-2f2d-4b76-a989-0fdc57edd143',
    newsIconsId = 'bd4f8c2e-a572-42c3-be3d-7457def94469',
    thinImagesId = 'cb79e894-8c47-45d8-afef-dbaade6d940b',
    cardsCarouselId = 'd805601c-5046-4d68-b9a5-61df941470dc',
  } = ids || {};

  const query = `{
    "textPages": *[_type == "textPage"]{
      _id,
      type,
      titleActive,
      titleInactive,
      titleInactiveItalic,
      paragraphText,
      buttonText,
      buttonLink,
    },
    "newsIcons": *[_type == "newsIcons" && _id == "${newsIconsId}"][0]{
      _id,
      items[] {
        icon,
        titleGrotesk,
        titleItalic,
        description
      }
    },
    "servicesSection": *[_type == "servicesSection" && _id == "${servicesSectionId}"][0]{
      _id,
      firstService {
        titleItalic,
        titleGrotesk,
        lightModeImage,
        darkModeImage,
      },
      secondService {
        titleItalic,
        titleGrotesk,
        lightModeImage,
        darkModeImage,
      }
    },
    "ourRangeData": *[_type == "ourRangeType" && _id == "${ourRangeId}"][0]{
      _id,
      items[] {
        title,
        lightModeImage,
        darkModeImage,
        description
      }
    },
    "brochureData": *[_type == "brochureType" && _id == "${brochureId}"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage
    },
    "thinImages": *[_type == "thinImages" && _id == "${thinImagesId}"][0]{
      _id,
      italicTitle,
      groteskTitle,
      grid,
      data[] {
        "titleItalic": italicTitle,
        "titleGrotesk": groteskTitle,
        description,
        lightModeImage,
        darkModeImage
      }
    },
    "cardsCarousel": *[_type == "imagesCarousel" && _id == "${cardsCarouselId}"][0]{
      _id,
      images[] {
        lightModeImage,
        darkModeImage,
        titleItalic,
        titleGrotesk,
        description,
        buttonText,
        buttonLink
      }
    }
  }`;

  try {
    const data = await client.fetch(query);

    const processedData = {
      textPages: data.textPages,
      servicesSection: data.servicesSection
        ? {
            ...data.servicesSection,
            firstService: data.servicesSection.firstService
              ? {
                  ...data.servicesSection.firstService,
                  lightModeImageUrl: urlFor(
                    data.servicesSection.firstService.lightModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                  darkModeImageUrl: urlFor(
                    data.servicesSection.firstService.darkModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                }
              : null,
            secondService: data.servicesSection.secondService
              ? {
                  ...data.servicesSection.secondService,
                  lightModeImageUrl: urlFor(
                    data.servicesSection.secondService.lightModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                  darkModeImageUrl: urlFor(
                    data.servicesSection.secondService.darkModeImage?.asset
                      ?._ref,
                    { width: 1200 },
                  ),
                }
              : null,
          }
        : null,
      ourRangeData: processImageUrlsSync(
        data.ourRangeData,
        {
          arrays: [
            { field: 'items', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        1200,
      ),
      brochureData: processImageUrlsSync(
        data.brochureData,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      newsIcons: processImageUrlsSync(data.newsIcons, {
        arrays: [{ field: 'items', itemFields: ['icon'] }],
      }),
      thinImages: processImageUrlsSync(
        data.thinImages,
        {
          arrays: [
            { field: 'data', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        800,
      ),
      cardsCarousel: processImageUrlsSync(
        data.cardsCarousel,
        {
          arrays: [
            {
              field: 'images',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        1200,
      ),
    };

    onProgress?.(1);

    return processedData;
  } catch (error) {
    console.error('Error fetching main services data:', error);
    return { error };
  }
};

export const getAboutPageData = async (ids, onProgress) => {
  const {
    rollingMenuId = '7d57e7d9-9429-4859-aabb-6e899a030508',
    brochureId = 'c7ea00dc-d35a-4e53-bd7a-bf9e529cf369',
    thinImagesId = 'cfa6af27-d2f8-4479-b304-945775417e6d',
    newsIconsId = 'fe393a21-669a-48f6-a69d-245c93fd54f3',
    homeSliderId = '10e45daf-07ff-4135-93f5-7e04be935520',
  } = ids || {};

  const query = `{
    "textPages": *[_type == "textPage"]{
      _id,
      type,
      titleActive,
      titleInactive,
      titleInactiveItalic,
      paragraphText,
      buttonText,
      buttonLink,
    },
    "newsIcons": *[_type == "newsIcons" && _id == "${newsIconsId}"][0]{
      _id,
      items[] {
        icon,
        titleGrotesk,
        titleItalic,
        description
      }
    },
    "aboutTeamMembers": *[_type == "aboutTeamMembers"][0]{
      _id,
      memberOne {
        titleItalic,
        titleGrotesk,
        description,
        linkSection {
          link,
          linkLabel
        },
        lightModeImage,
        darkModeImage
      },
      memberTwo {
        titleItalic,
        titleGrotesk,
        description,
        linkSection {
          link,
          linkLabel
        },
        lightModeImage,
        darkModeImage
      },
      memberThree {
        titleItalic,
        titleGrotesk,
        description,
        linkSection {
          link,
          linkLabel
        },
        lightModeImage,
        darkModeImage
      }
    },
    "thinImages": *[_type == "thinImages" && _id == "${thinImagesId}"][0]{
      _id,
      italicTitle,
      groteskTitle,
      grid,
      data[] {
        "titleItalic": italicTitle,
        "titleGrotesk": groteskTitle,
        description,
        lightModeImage,
        darkModeImage
      }
    },
    "rollingMenu": *[_type == "rollingMenu" && _id == "${rollingMenuId}"][0]{
      _id,
      leftSideTitle,
      data[] {
        lightModeImage,
        darkModeImage,
        leftSideTitle,
        title,
        description
      }
    },
    "brochureData": *[_type == "brochureType" && _id == "${brochureId}"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage
    },
    "homeSlider": *[_type == "homeSlider" && _id == "${homeSliderId}"][0]{
      _id,
      slides[] {
        lightModeImage,
        darkModeImage,
        title,
        opacityTitle,
        italicTitle,
        description,
        buttonText,
        buttonLink
      }
    }
  }`;

  try {
    const data = await client.fetch(query);

    const processedData = {
      textPages: data.textPages,
      aboutTeamMembers: data.aboutTeamMembers
        ? [
            {
              ...data.aboutTeamMembers.memberOne,
              link: data.aboutTeamMembers.memberOne?.linkSection?.link,
              linkLabel:
                data.aboutTeamMembers.memberOne?.linkSection?.linkLabel,
              lightModeImageUrl: urlFor(
                data.aboutTeamMembers.memberOne?.lightModeImage?.asset?._ref,
                { width: 800 },
              ),
              darkModeImageUrl: urlFor(
                data.aboutTeamMembers.memberOne?.darkModeImage?.asset?._ref,
                { width: 800 },
              ),
            },
            {
              ...data.aboutTeamMembers.memberTwo,
              link: data.aboutTeamMembers.memberTwo?.linkSection?.link,
              linkLabel:
                data.aboutTeamMembers.memberTwo?.linkSection?.linkLabel,
              lightModeImageUrl: urlFor(
                data.aboutTeamMembers.memberTwo?.lightModeImage?.asset?._ref,
                { width: 800 },
              ),
              darkModeImageUrl: urlFor(
                data.aboutTeamMembers.memberTwo?.darkModeImage?.asset?._ref,
                { width: 800 },
              ),
            },
            {
              ...data.aboutTeamMembers.memberThree,
              link: data.aboutTeamMembers.memberThree?.linkSection?.link,
              linkLabel:
                data.aboutTeamMembers.memberThree?.linkSection?.linkLabel,
              lightModeImageUrl: urlFor(
                data.aboutTeamMembers.memberThree?.lightModeImage?.asset?._ref,
                { width: 800 },
              ),
              darkModeImageUrl: urlFor(
                data.aboutTeamMembers.memberThree?.darkModeImage?.asset?._ref,
                { width: 800 },
              ),
            },
          ]
        : [],
      thinImages: data.thinImages
        ? {
            ...data.thinImages,
            data: data.thinImages.data.map((item) => ({
              ...item,
              lightModeImageUrl: urlFor(item.lightModeImage?.asset?._ref, {
                width: 800,
              }),
              darkModeImageUrl: urlFor(item.darkModeImage?.asset?._ref, {
                width: 800,
              }),
            })),
          }
        : null,
      rollingMenu: processImageUrlsSync(
        data.rollingMenu,
        {
          arrays: [
            { field: 'data', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        1200,
      ),
      brochureData: processImageUrlsSync(
        data.brochureData,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      newsIcons: processImageUrlsSync(data.newsIcons, {
        arrays: [{ field: 'items', itemFields: ['icon'] }],
      }),
      homeSlider: processImageUrlsSync(
        data.homeSlider,
        {
          arrays: [
            {
              field: 'slides',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        1200,
      ),
    };

    onProgress?.(1);

    return processedData;
  } catch (error) {
    console.error('Error fetching about page data:', error);
    return { error };
  }
};

export const getB2CServicesData = async (ids, onProgress) => {
  const {
    cardsCarouselId = '72fe232c-233f-4adb-853d-307c33144e5f',
    brochureId = 'c0eb3302-ed67-47f9-87d5-a79bf2ea431d',
    fullPageImageCardId = 'e9de5f9e-291a-4871-bf1c-d0ec4dcd9834',
    showRoomsId = '08e52c28-5350-4643-8b14-a4f7d3389b8c',
  } = ids || {};

  const query = `{
    "textPages": *[_type == "textPage"]{
      _id,
      type,
      titleActive,
      titleInactive,
      titleInactiveItalic,
      paragraphText,
      buttonText,
      buttonLink,
    },
    "cardsCarousel": *[_type == "imagesCarousel" && _id == "${cardsCarouselId}"][0]{
      _id,
      images[] {
        lightModeImage,
        darkModeImage,
        titleItalic,
        titleGrotesk,
        description,
        buttonText,
        buttonLink
      }
    },
    "brochureData": *[_type == "brochureType" && _id == "${brochureId}"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage
    },
    "fullPageImageCard": *[_type == "fullPageImageCard" && _id == "${fullPageImageCardId}"][0]{
      _id,
      number,
      title,
      description,
      lightModeImage,
      darkModeImage
    },
    "showRoomsData": *[_type == "showRoomsType" && _id == "${showRoomsId}"][0]{
      _id,
      description,
      items[] {
        title,
        mainTitle,
        lightModeImage,
        darkModeImage
      }
    }
  }`;

  try {
    const data = await client.fetch(query);

    const processedData = {
      textPages: data.textPages,
      cardsCarousel: processImageUrlsSync(
        data.cardsCarousel,
        {
          arrays: [
            {
              field: 'images',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        1200,
      ),
      brochureData: processImageUrlsSync(
        data.brochureData,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      fullPageImageCard: processImageUrlsSync(
        data.fullPageImageCard,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        800,
      ),
      showRoomsData: processImageUrlsSync(
        data.showRoomsData,
        {
          arrays: [
            { field: 'items', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        1200,
      ),
    };

    onProgress?.(1);

    return processedData;
  } catch (error) {
    console.error('Error fetching B2C services data:', error);
    return { error };
  }
};

export const getB2BServicesData = async (ids, onProgress) => {
  const {
    brochureId = 'cd9307de-2cef-43b3-aa4a-84be7fa939d6',
    showRoomsId = '09bd3446-aee0-48b8-85f9-4674eae2c18b',
  } = ids || {};

  const query = `{
    "textPages": *[_type == "textPage"]{
      _id,
      type,
      titleActive,
      titleInactive,
      titleInactiveItalic,
      paragraphText,
      buttonText,
      buttonLink,
    },
    "presentationInNumbers": *[_type == "presentationInNumbers"][0]{
      _id,
      section1{
        number,
        title,
        description
      },
      section2{
        number,
        title,
        description
      },
      section3{
        number,
        title,
        description
      },
      section4{
        number,
        title,
        description
      }
    },
    "clientData": *[_type == "clientType"][0]{
      _id,
      name,
      showRoomTitle,
      year,
      tags,
      images[] {
        name,
        lightModeImage,
        darkModeImage
      }
    },
    "brochureData": *[_type == "brochureType" && _id == "${brochureId}"][0]{
      _id,
      title,
      titleHighlight,
      description,
      buttonText,
      buttonLink,
      lightModeImage,
      darkModeImage
    },
    "showRoomsData": *[_type == "showRoomsType" && _id == "${showRoomsId}"][0]{
      _id,
      description,
      items[] {
        title,
        mainTitle,
        lightModeImage,
        darkModeImage
      }
    }
  }`;

  try {
    const data = await client.fetch(query);

    const processedData = {
      textPages: data.textPages,
      presentationInNumbers: data.presentationInNumbers,
      clientData: processImageUrlsSync(
        data.clientData,
        {
          arrays: [
            {
              field: 'images',
              itemFields: ['lightModeImage', 'darkModeImage'],
            },
          ],
        },
        800,
      ),
      brochureData: processImageUrlsSync(
        data.brochureData,
        {
          single: ['lightModeImage', 'darkModeImage'],
        },
        1920,
      ),
      showRoomsData: processImageUrlsSync(
        data.showRoomsData,
        {
          arrays: [
            { field: 'items', itemFields: ['lightModeImage', 'darkModeImage'] },
          ],
        },
        1200,
      ),
    };

    onProgress?.(1);

    return processedData;
  } catch (error) {
    console.error('Error fetching B2B services data:', error);
    return { error };
  }
};
